import { ComponentType } from 'react';

enum DrawerId {
  BalanceAi = 'balance-ai',
  BalanceAiManageSubscriptions = 'balance-ai/manage-subscriptions',
}

/**
 * Props the manager injects into every registered drawer. A drawer never
 * receives these from the caller — they come from its position in the stack.
 */
type DrawerInjectedProps = {
  open: boolean;
  onClose: () => void;
};

/** Use for drawers that take nothing beyond the injected props. */
type EmptyDrawerProps = Record<never, never>;

/**
 * The props each drawer accepts from whoever opens it. Keep these
 * serializable — ids and primitives, not hook return values or query results.
 */
interface DrawerPropsMap {
  [DrawerId.BalanceAi]: EmptyDrawerProps;
  [DrawerId.BalanceAiManageSubscriptions]: EmptyDrawerProps;
}

type DrawerComponent<T extends DrawerId> = ComponentType<
  DrawerPropsMap[T] & DrawerInjectedProps
>;

type RegistryEntry<T extends DrawerId = DrawerId> = {
  loader: () => Promise<{ default: DrawerComponent<T> }>;
};

/**
 * Loaders must import the drawer's own module, never a barrel — a barrel pulls
 * the registry back in and creates a cycle.
 */
const DRAWER_REGISTRY: { [T in DrawerId]: RegistryEntry<T> } = {
  [DrawerId.BalanceAi]: {
    loader: () =>
      import(
        'src/pages/revision/balance-ai/balance-ai-drawer/balance-ai-drawer.container'
      ).then((m) => ({ default: m.BalanceAiDrawer })),
  },
  [DrawerId.BalanceAiManageSubscriptions]: {
    loader: () =>
      import(
        'src/pages/revision/balance-ai/balance-ai-drawer/manage-subscriptions-drawer/manage-subscriptions.component'
      ).then((m) => ({ default: m.ManageSubscriptionsDrawer })),
  },
};

export { DRAWER_REGISTRY, DrawerId };
export type {
  DrawerComponent,
  DrawerInjectedProps,
  DrawerPropsMap,
  EmptyDrawerProps,
  RegistryEntry,
};
